import type { Timestamp } from '@app/_proto/google/protobuf/timestamp';
import useTimeCountdown from '@app/domains/common/hooks/useTimeCountdown';
import { timestampToDate } from '@app/libs/utils';
import { useMemo } from 'react';

const timeUnits = {
    DD: 'days',
    HH: 'hrs',
    mm: 'min',
    ss: 'sec',
};

const remainingTimeFormat = 'DD:HH:mm:ss';

type Props = {
    endsAt: Timestamp;
    countdownEnded?: () => void;
};

export default function Countdown({ endsAt, countdownEnded }: Props) {
    const remainingTime = useTimeCountdown({
        futureDate: timestampToDate(endsAt) ?? undefined,
        format: remainingTimeFormat,
        onTimeUp() {
            countdownEnded?.();
        },
    });

    const splitFormat = useMemo(() => {
        return remainingTimeFormat?.split(':');
    }, []);

    const splitRemainingTime = useMemo(() => {
        return remainingTime?.split(':');
    }, [remainingTime]);

    if (!splitRemainingTime) {
        return null;
    }

    return (
        <div className="flex justify-evenly gap-3 text-center whitespace-nowrap leading-tight">
            {splitFormat.map((unitKey, index) => {
                const val = splitRemainingTime[index];
                const unit = timeUnits[unitKey];

                return <TimeSlice key={`${val}-${unit}`} val={val} unit={unit} />;
            })}
        </div>
    );
}

const TimeSlice = ({ val, unit }: { val: string; unit: string }) => {
    return (
        <>
            <div className="first:hidden">
                <div className="text-[12px] font-bold">:</div>
                <div className="text-[8px]"> </div>
            </div>
            <div>
                <div className="text-[12px] font-bold w-[2ch] mx-auto">{val}</div>
                <div className="uppercase text-[8px]">{unit}</div>
            </div>
        </>
    );
};
